// FNP-1191 — READ HALF ONLY. There is deliberately no mutations.ts.
//
// The FNP-1184 schema exposes no zone create/update/delete: writes are spec 2
// and carry their own decisions (change batching, conflict handling, what a
// partial apply means). The port methods and the GraphQL mutations do not exist
// yet, so a mutations file here would be a call to a field that is not on the
// schema — a runtime validation error, not a TODO.

// ⚠️ `failures` IS SELECTED ON THE LIST, AND THAT IS THE POINT OF THIS MODULE.
//
// Omitting it would make a tenant with a broken connection see a SHORTER list
// with no explanation — indistinguishable from having deleted those zones. The
// four failure fields are all rendered: which connection, which provider, the
// provider's verbatim message, and whether retrying could plausibly work.
// ⚠️ `recordCount` AND `providerZoneId` ARE SELECTED HERE, ON BOTH OPERATIONS.
//
// Both are nullable, and an unselected nullable field is indistinguishable at
// runtime from one the provider did not report: `undefined` either way, with
// nothing in the type system to say which. That is the write-path-without-
// read-path defect this app has now shipped three times (FNP-1360, FNP-1369,
// FNP-1377), and the reason zones/tests/queries.test.ts asserts on this string.
//
// For `recordCount` the failure would be quiet and wrong in a specific
// direction: every zone would render "not reported" forever, which looks like
// a provider limitation rather than a missing line in a query.
// ⚠️ THE THREE FNP-1423 DELEGATION FIELDS ARE SELECTED HERE TOO, AND THE
// REASON IS SHARPER THAN FOR THE TWO ABOVE.
//
// `delegationStatus` is `ZoneDelegationStatus!` — non-null — precisely so that
// "we do not know" arrives as the named member NOT_DETERMINED rather than as a
// null the client cannot tell from a field it forgot to ask for. Not selecting
// it throws that design away: every row would render NOT_DETERMINED forever,
// which looks exactly like an API that cannot answer rather than a query
// missing a line. That is the same read-path defect as FNP-1360/1369/1377, and
// this module has now shipped it three times.
//
// `delegationUndeterminedReason` is selected WITH it, never separately. A
// NOT_DETERMINED with no reason is the bare "unknown" the API side went to
// trouble to avoid — the sentence is what stops an operator deleting a live
// zone on the strength of a DNS timeout.
//
// Verified against the DEPLOYED DEV schema (appsync get-introspection-schema,
// api ia35gxdm75dbfabnw5jbvrhgem, 2026-09-07) before being added here, not
// against the source tree — a built artifact is not proof of a deployment.
const DNS_ZONE_FIELDS = `
    id
    name
    connectionId
    provider
    isPrivate
    nameservers
    appearsDelegated
    delegationStatus
    delegationParentName
    delegationUndeterminedReason
    recordCount
    providerZoneId
`;

const DNS_CONNECTION_FAILURE_FIELDS = `
    connectionId
    provider
    kind
    message
    retryable
`;

export const listDnsZones = `
   query listDnsZones($connectionId: ID, $limit: Int, $nextToken: String) {
       listDnsZones(connectionId: $connectionId, limit: $limit, nextToken: $nextToken) {
           items {${DNS_ZONE_FIELDS}}
           nextToken
           failures {${DNS_CONNECTION_FAILURE_FIELDS}}
       }
   }
`;

// ⚠️ BOTH ARGUMENTS ARE REQUIRED (`ID!`). A zone id is only meaningful against
// the connection that minted it — the same zone name under two Route 53
// connections is two different zones, and `id` alone cannot tell them apart.
export const getDnsZone = `
   query getDnsZone($connectionId: ID!, $id: ID!) {
       getDnsZone(connectionId: $connectionId, id: $id) {${DNS_ZONE_FIELDS}}
   }
`;

// The connection's capability snapshot, for the write-control hiding decision
// (FNP-1181). Only the capability fields are selected: this module never needs
// the secret ref, and not asking for it is one fewer place it could be logged.
export const getProviderConnectionCapabilities = `
   query getProviderConnection($id: ID!) {
       getProviderConnection(id: $id) {
           id
           status
           capabilities {
               canWriteRecords
               canCreateZones
               canDeleteZones
               canSetNameservers
           }
       }
   }
`;

// ─── FNP-1191 CRUD half ───
//
// The connections a zone can be CREATED through. The create form needs a
// connection picker, and a zone is only addressable via the connection that
// mints its id — so "which connection" is a required choice, not an inferred one
// (a tenant with two Route 53 accounts has two connections and the same zone
// name could live under either).
//
// ⚠️ `capabilities` AND `status` ARE BOTH SELECTED, because the picker must
// offer only connections that can actually create a zone. Listing a read-only
// or INVALID connection would produce a PERMISSION_DENIED at submit — the exact
// failure the capability-hiding rule exists to prevent, reintroduced one level
// down in the form.
//
// `secretRef` is deliberately NOT selected: this module never needs it, and not
// asking is one fewer place it could be logged.
export const listProviderConnectionsForZoneCreate = `
   query listProviderConnections($limit: Int, $nextToken: String) {
       listProviderConnections(limit: $limit, nextToken: $nextToken) {
           items {
               id
               displayName
               providerId
               status
               capabilities {
                   canWriteRecords
                   canCreateZones
                   canDeleteZones
                   canSetNameservers
               }
           }
           nextToken
       }
   }
`;
